import React, { memo, useCallback, useReducer, useEffect } from 'react';
import { Form } from '@redlink/components/organisms';
import { Space } from '@redlink/components/atoms';
import {
  FormSection,
  TextInput,
  DropDown,
  LogoItem,
  LogoItemHorizontal,
  usePlan,
} from '@redlink/components/molecules';
import { useLocale } from '@redlink/assets/locale';
import { useValues } from '@redlink/assets/values';
import {
  useMutation,
  useQuery,
  generateNewShortLinkQuery,
  newLinkQuery,
  editLinkQuery,
  getPageProfileQuery,
} from '@redlink/services';
import { initialState, reducer, types } from './store';
import { freshCache } from './cache';

export const LinkEditTemplate = memo(
  ({ onSubmit, onDismiss, pageName, data, onChangeItem, style }) => {
    const { dictionary } = useLocale();
    const { getPageLink, staticData } = useValues();
    const { showPlanDialog } = usePlan();

    const [
      { id, logoName, title, url, token, isEditMode, index: row_index },
      dispatch,
    ] = useReducer(reducer, initialState(data));

    useEffect(
      () => {
        const initData = initialState(data);
        dispatch({ type: types.SET_MULTI, payload: initData });

        reflectChanges(initData);

        !initData.token && newShortLink();
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [data]
    );

    const renderLogoItem = useCallback(
      (item, index) => <LogoItem {...{ item }} selectedItem={logoName} />,
      [logoName]
    );

    const renderLogoSelectedItem = useCallback(
      () => <LogoItemHorizontal item={logoName} />,
      [logoName]
    );

    const [newShortLink, { loading: newShortLinkLoading }] = useMutation(
      generateNewShortLinkQuery,
      {
        done: ({ GenerateNewShortLink: { token } }) =>
          dispatch({ type: types.SET_TOKEN, payload: token }),
      }
    );

    const [submitLink, { loading: submitLinkIsLoading }] = useMutation(
      !isEditMode ? newLinkQuery : editLinkQuery,
      {
        done: () => onDismiss(),
        update: freshCache({ isEditMode, id, pageName }),
        limited: ({ maxCount }) => {
          onDismiss && onDismiss();
          showPlanDialog();
        },
      }
    );

    const reflectChanges = (change) => {
      onChangeItem &&
        onChangeItem({
          id,
          logoName,
          title,
          url,
          shortLink: token,
          ...change,
        });
    };

    const {
      data: { PageProfile: { currentPlan } = {} },
    } = useQuery(getPageProfileQuery);

    return (
      <Form
        {...{ style }}
        isBusy={newShortLinkLoading || submitLinkIsLoading}
        actions={[
          {
            title: dictionary.cancel,
            variant: 'secondary',
            onPress: onDismiss,
          },
          {
            title: dictionary.add,
            variant: 'primary',
            onPress: () => {
              onSubmit
                ? onSubmit({
                    id,
                    logoName,
                    title,
                    url,
                    token,
                    index: row_index,
                  })
                : submitLink({
                    id,
                    logoName,
                    title,
                    url,
                    shortLink: token,
                  });
            },
          },
        ]}
      >
        {(containerLayout) => (
          <FormSection title={dictionary.newLink}>
            <DropDown
              require
              text={logoName}
              horizontal
              {...{ containerLayout }}
              data={staticData.logoList}
              renderItem={renderLogoItem}
              renderSelectedItem={renderLogoSelectedItem}
              placeholder={dictionary.selectLinkLogo}
              onChange={(item) => {
                dispatch({
                  type: types.SET_LOGO_NAME,
                  payload: item,
                });
                reflectChanges({ logoName: item });
              }}
            />
            <Space size={16} />
            <TextInput
              require
              title={dictionary.title}
              value={title}
              onChangeText={(value) => {
                dispatch({ type: types.SET_TITLE, payload: value });
                reflectChanges({ title: value });
              }}
            />
            <Space size={16} />
            <TextInput
              require
              en
              title={dictionary.url}
              value={url}
              onChangeText={(value) => {
                dispatch({ type: types.SET_URL, payload: value });
                reflectChanges({ url: value });
              }}
            />
            <Space size={16} />
            <TextInput
              editable={currentPlan.isProUser}
              withCrownIcon
              {...(!currentPlan.isProUser && {
                description: dictionary.basicLimitationHintDescription,
              })}
              require
              en
              title={dictionary.shortLink}
              prefix={`${getPageLink(pageName)}/`}
              value={token}
              onChangeText={(value) => {
                dispatch({ type: types.SET_TOKEN, payload: value });
                reflectChanges({ token: value });
              }}
            />
          </FormSection>
        )}
      </Form>
    );
  }
);
